<template>
  <div class="consultant">
    <div class="header">
      <button type="button" @click.stop="toggleMenu" class="burger-button">☰</button>
      <button
        type="button"
        class="theme-button"
        @click.stop="basculerTheme"
        :aria-label="themeSombre ? $t('theme.switchToLight') : $t('theme.switchToDark')">
        {{ themeSombre ? '☀' : '☾' }}
      </button>
      <h1>Thierry Martin consultant en informatique</h1>
    </div>
    <div class="content" :class="{ 'content-overlay': store.menuVisible && isMobile }">
      <div class="menu" :class="{ 'menu-overlay': store.menuVisible && isMobile }" ref="menu">
        <ul v-if="store.menuVisible">
          <li
            v-for="(section, index) in sections"
            :key="index"
            tabindex="0"
            @click="handleMenuClick(section.value)"
            @keydown="handleKeydown(section.value, $event, index)"
            :ref="'menuItem'">
            {{ section.label }}
          </li>
        </ul>
        <div v-else-if="!isMobile">
          <router-link to="/" aria-label="Aller à l'accueil">
            <img :src="logoActif" alt="Logo de Thierry Martin consultant informatique" />
          </router-link>
        </div>
      </div>
      <component :is="currentComponent" />
    </div>
    <div class="footer">
      <router-link
        class="footer-link"
        to="/mentions_legales"
        aria-label="Voir les mentions légales">
        <small>© Thierry Martin {{ copyrightYears }} – Développé avec VueJS par mes soins</small>
      </router-link>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import {
  useRoute, useRouter,
} from 'vue-router';
import logoTm from '@/assets/images/logo_tm.webp';
import logoTmDark from '@/assets/images/logo_tm_dark.webp';
import DeviceUtils from '@/utils/device';
import useNavigationStore from '@/stores/navigation';
import useThemeStore from '@/stores/theme';
import Meta from '@/composables/useMeta';
import sections from '@/constants/sections.json';
import DetailsSection from './DetailsSection.vue';
import ContactForm from './ContactForm.vue';
import MentionsLegales from './MentionsLegales.vue';

export default defineComponent({
  name: 'MartinThierry',

  components: {
    DetailsSection,
    ContactForm,
    MentionsLegales,
  },

  data() {
    return {
      store: useNavigationStore(),
      themeStore: useThemeStore(),
      route: useRoute(),
      router: useRouter(),
      deviceUtils: DeviceUtils,
      windowWidth: window.innerWidth,
      currentComponent: 'DetailsSection',
      sections,
      metaUtils: new Meta(),
      buildDate: process.env.VUE_APP_BUILD_DATE || new Date().toISOString(),
      logoTm,
      logoTmDark,
    };
  },

  computed: {
    isMobile(): boolean {
      return this.deviceUtils.isMobile();
    },
    buildYear(): number {
      const parsedDate = new Date(this.buildDate);
      if (Number.isNaN(parsedDate.getTime())) {
        return new Date().getFullYear();
      }
      return parsedDate.getFullYear();
    },
    copyrightYears(): string {
      return `2025–${this.buildYear}`;
    },
    themeSombre(): boolean {
      return this.themeStore.estSombre;
    },
    logoActif(): string {
      return this.themeSombre ? this.logoTmDark : this.logoTm;
    },
  },

  watch: {
    $route() {
      this.updateSectionFromRoute();
      this.updateCanonicalUrl();
    },
  },

  mounted() {
    this.store.initializePageFromUrl();
    this.updateSectionFromRoute();
    this.updateCanonicalUrl();
    this.windowWidth = window.innerWidth;
    window.addEventListener('resize', this.handleResize);
    document.addEventListener('keydown', this.handleKeydownGlobal);
    document.addEventListener('click', this.handleOutsideClick);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
    document.removeEventListener('keydown', this.handleKeydownGlobal);
    document.removeEventListener('click', this.handleOutsideClick);
  },

  methods: {
    updateSectionFromRoute() {
      const section = this.route.params.section || 'accueil';
      const selected = this.sections.find((sectionSelectionnee) => sectionSelectionnee.value
       === section) || this.sections[0];
      this.selectSection(selected.value);
    },

    updateCanonicalUrl() {
      const currentSection = this.route.params.section || 'accueil';
      const path = currentSection === 'accueil'
        ? '/'
        : `/${currentSection}`;
      this.metaUtils.updateCanonicalUrl(path);
    },

    toggleMenu(): void {
      this.store.toggleMenu();
    },

    basculerTheme(): void {
      this.themeStore.basculerTheme();
    },

    selectSection(section: string): void {
      if (section === 'cv') {
        this.store.navigateToExternalPage('/cv-ref-tech-martin_thierry.pdf');
        return;
      }
      if (section === 'contact') {
        this.currentComponent = 'ContactForm';
      } else if (section === 'mentions_legales') {
        this.currentComponent = 'MentionsLegales';
      } else {
        this.currentComponent = 'DetailsSection';
      }
      this.store.navigateTo(section);
      const routePath = section === 'accueil' ? '/' : `/${section}`;
      this.router.push(routePath);
    },

    handleKeydown(section: string, event: KeyboardEvent, index: number): void {
      if (event.key === 'Enter' || event.key === ' ') {
        this.selectSection(section);
        if (this.isMobile) {
          this.store.hideMenu();
        }
      } else if (event.key === 'Escape') {
        this.store.hideMenu();
      } else if (event.key === 'ArrowDown') {
        event.preventDefault();
        const nextIndex = (index + 1) % this.sections.length;
        const nextItem = (this.$refs.menuItem as HTMLElement[])[nextIndex];
        nextItem.focus();
      } else if (event.key === 'ArrowUp') {
        event.preventDefault();
        const prevIndex = (
          index - 1 + this.sections.length
        ) % this.sections.length;
        const prevItem = (this.$refs.menuItem as HTMLElement[])[prevIndex];
        prevItem.focus();
      }
    },

    handleKeydownGlobal(event: KeyboardEvent): void {
      if (event.key === 'Escape' && this.store.menuVisible) {
        this.store.hideMenu();
      }
    },

    handleMenuClick(section: string): void {
      this.selectSection(section);
      if (this.isMobile) {
        this.store.hideMenu();
      }
    },

    handleResize() {
      this.windowWidth = window.innerWidth;
      if (this.isMobile && this.store.menuVisible) {
        this.store.hideMenu();
      }
    },

    handleOutsideClick(event: MouseEvent) {
      if (this.store.menuVisible && this.isMobile) {
        const target = event.target as HTMLElement;
        const isInsideMenu = target.closest('.burger-button, .menu');
        if (!isInsideMenu) {
          this.store.hideMenu();
        }
      }
    },
  },
});
</script>

<style scoped lang="stylus">
@import '../styles/variables.styl'

.consultant
  display flex
  flex-direction column
  flex 1
  min-height 100%

.header
  display flex
  align-items center
  width 100%
  box-sizing border-box
  text-align center
  position relative
  z-index 20
  margin-bottom 1rem

  button
    margin 0 1rem
    text-align center

  h1
    flex 1
    font-size 1.5rem

.content
  display block
  width 100%
  position relative
  flex 1

  &.content-overlay
    background-color var(--couleur-overlay)

.menu
  margin-right 0

  &.menu-overlay
    position absolute
    top 0
    left 0
    width 100%
    height auto
    background-color var(--couleur-fond-menu-overlay)
    z-index 10

  ul
    list-style none
    padding 0.5rem
    margin 0
    background-color var(--couleur-fond-menu)

    li
      padding 0.5rem
      text-align center

.details
  width: 100%

.footer
  text-align center
  padding 1rem 0 0
  color var(--couleur-texte-secondaire)
  background-color transparent
  margin-top auto

.footer-link
  color inherit
  text-decoration none

  &:hover
    text-decoration underline

@media (min-width: $max-width-petit-smartphone + 1)
  .header
    margin-bottom 3rem

    h1
      font-size 2rem

  .content
    display flex

  .menu
    flex 1
    margin-right 20px

  .details
    flex 3
    width auto
</style>
